import { Service, ServiceSellPrice } from '@turizi/shared';
import { useTranslation } from 'react-i18next';
import { Button, NumberInput, Loader, Switch } from '@mantine/core';
import { IconClock } from '@tabler/icons-react';
import { ServiceTitleStatic } from '../services/service-title';
import DatePickerCustom from '../custom-date-picker';
import { DEFAULT_CURRENCY } from '@turizi/shared';
import { formatDayLong, formatPrice } from '@/lib/format-utils';
import { useState } from 'react';
import { useCreateServiceSellPrice } from '@/hooks/api-hooks';
import { notify } from '@/lib/notify';

interface PricesTabProps {
    services: Service[];
}

interface ServiceUpdateStatus {
    id: Service['id'];
    loading: boolean;
    success: boolean | null;
}

const DELAY = 350; // ms

export function BulkEditPricesTab({ services }: PricesTabProps) {
    const { t } = useTranslation();
    const createPriceMutation = useCreateServiceSellPrice();

    const [priceFactor, setPriceFactor] = useState<number>(1);
    const [startDay, setStartDay] = useState<string>('');
    const [endDay, setEndDay] = useState<string>('');
    const [applyToLinkedService, setApplyToLinkedService] = useState<boolean>(false);
    const [updateStatuses, setUpdateStatuses] = useState<Record<Service['id'], ServiceUpdateStatus>>({});
    const [counter, setCounter] = useState<number>(0);

    const hasStarted = Object.keys(updateStatuses).length > 0;
    const allCompleted = hasStarted && Object.values(updateStatuses).every(status => !status.loading);

    const DEFAULT_SERVICE_SELL_PRICE: Partial<ServiceSellPrice> = {
        default_amount: 1,
        default_currency: DEFAULT_CURRENCY,
        child_amount: null,
        child_currency: DEFAULT_CURRENCY,
        senior_amount: null,
        senior_currency: DEFAULT_CURRENCY,
        infant_amount: null,
        infant_currency: DEFAULT_CURRENCY,
    };

    const getLastPrice = (service: Service): Partial<ServiceSellPrice> => {
        const activePrices = service?.sell_prices?.filter(p => p.status === 'active');
        if (activePrices?.length > 0) {
            return activePrices?.[activePrices?.length - 1];
        }
        return DEFAULT_SERVICE_SELL_PRICE;
    };

    const handleBulkAddPrice = async () => {
        if (!endDay) {
            notify.error(t('error'), t('please_select_end_date'));
            return;
        }

        // Initialize statuses
        const initialStatuses: Record<Service['id'], ServiceUpdateStatus> = {};
        services.forEach(service => {
            initialStatuses[service.id] = { id: service.id, loading: true, success: null };
        });
        setUpdateStatuses(initialStatuses);
        setCounter(0);

        // Add price to each service
        let completed = 0;
        for (let i = 0; i < services.length; i++) {
            const service = services[i];

            if (i > 0) {
                await new Promise(resolve => setTimeout(resolve, DELAY));
            }

            const lastPrice = getLastPrice(service);
            const calculatedDefault = (lastPrice.default_amount || 0) * priceFactor;
            const calculatedChild = lastPrice.child_amount ? lastPrice.child_amount * priceFactor : null;
            const calculatedSenior = lastPrice.senior_amount ? lastPrice.senior_amount * priceFactor : null;
            const calculatedInfant = lastPrice.infant_amount ? lastPrice.infant_amount * priceFactor : null;

            const startDayForService = startDay || lastPrice.end_day || '2000-01-01';

            const priceData: Partial<ServiceSellPrice> & { apply_to_linked_service?: boolean } = {
                service_id: service.id,
                default_amount: calculatedDefault,
                default_currency: lastPrice.default_currency || DEFAULT_CURRENCY,
                child_amount: calculatedChild,
                child_currency: calculatedChild ? lastPrice.child_currency || DEFAULT_CURRENCY : null,
                senior_amount: calculatedSenior,
                senior_currency: calculatedSenior ? lastPrice.senior_currency || DEFAULT_CURRENCY : null,
                infant_amount: calculatedInfant,
                infant_currency: calculatedInfant ? lastPrice.infant_currency || DEFAULT_CURRENCY : null,
                start_day: startDayForService,
                end_day: endDay,
                status: 'active',
                start_hour: null,
                end_hour: null,
                timezone: 'UTC',
                apply_to_linked_service: applyToLinkedService,
            };

            try {
                await createPriceMutation.mutateAsync(priceData);

                completed++;
                setCounter(completed);
                setUpdateStatuses(prev => ({
                    ...prev,
                    [service.id]: { id: service.id, loading: false, success: true }
                }));
            } catch (error) {
                completed++;
                setCounter(completed);
                setUpdateStatuses(prev => ({
                    ...prev,
                    [service.id]: { id: service.id, loading: false, success: false }
                }));
            }
        }
    };

    return (
        <>
            <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <DatePickerCustom
                        label={`${t('start_date')} (${t('optional')})`}
                        placeholder={t('start_date')}
                        value={startDay}
                        onChange={(val: any) => {
                            const d = Array.isArray(val) ? (val[0] || val[1] || '') : (val || '');
                            setStartDay(d);
                        }}
                        clearable={true}
                    />
                    <DatePickerCustom
                        label={t('end_date')}
                        placeholder={t('end_date')}
                        value={endDay}
                        onChange={(val: any) => {
                            const d = Array.isArray(val) ? (val[0] || val[1] || '') : (val || '');
                            setEndDay(d);
                        }}
                        clearable={false}
                    />
                </div>

                <NumberInput
                    label={t('price_factor')}
                    description={t('multiply_last_price_by_this_factor')}
                    value={priceFactor}
                    onChange={(value) => setPriceFactor(Number(value))}
                    step={0.1}
                    min={0.1}
                    decimalScale={2}
                />

                <Switch
                    label={t('also_update_linked_service')}
                    checked={applyToLinkedService}
                    onChange={(e) => setApplyToLinkedService(e.currentTarget.checked)}
                    color="black"
                />

                <div className="text-sm font-medium mb-2">
                    {t('services_with_calculated_prices')}
                </div>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                    {services?.map(service => {
                        const lastPrice = getLastPrice(service);
                        const new_default_amount = (lastPrice.default_amount || 0) * priceFactor;
                        const new_child_amount = lastPrice.child_amount ? lastPrice.child_amount * priceFactor : null;
                        const new_senior_amount = lastPrice.senior_amount ? lastPrice.senior_amount * priceFactor : null;
                        const new_infant_amount = lastPrice.infant_amount ? lastPrice.infant_amount * priceFactor : null;
                        const currency = lastPrice.default_currency || DEFAULT_CURRENCY;
                        const newStartDay = startDay || lastPrice.end_day || '2000-01-01';

                        return (
                            <div key={service.id} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                                <div className="flex items-center gap-2 mb-3">
                                    <IconClock size={16} className="text-gray-500" />
                                    <span className="text-sm font-medium text-gray-700">
                                        {ServiceTitleStatic(service)}
                                    </span>
                                </div>

                                <div className="text-xs text-gray-500 mb-2">
                                    {t('last_price')}: <span className="font-semibold">{formatDayLong(lastPrice.start_day)} - {formatDayLong(lastPrice.end_day)}</span>
                                    {endDay && (
                                        <span className="ml-2 text-blue-600 font-semibold">
                                            ({t('new_price')}: {formatDayLong(newStartDay)} - {formatDayLong(endDay)})
                                        </span>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    {/* Adults / Default */}
                                    <div className="flex items-center justify-between text-xs bg-blue-50 p-2 rounded">
                                        <span className="font-medium text-gray-600">{t('adults')}</span>
                                        <div className="flex items-center gap-2">
                                            <span className="text-gray-600">{formatPrice(lastPrice.default_amount || 0)} {currency}</span>
                                            <span className="text-gray-400">→</span>
                                            <span className="font-bold text-green-600">{formatPrice(new_default_amount)} {currency}</span>
                                        </div>
                                    </div>

                                    {/* Children */}
                                    {lastPrice.child_amount && (
                                        <div className="flex items-center justify-between text-xs bg-white p-2 rounded">
                                            <span className="font-medium text-gray-600">{t('children')}</span>
                                            <div className="flex items-center gap-2">
                                                <span className="text-gray-600">{formatPrice(lastPrice.child_amount)} {currency}</span>
                                                <span className="text-gray-400">→</span>
                                                <span className="font-bold text-green-600">{formatPrice(new_child_amount)} {currency}</span>
                                            </div>
                                        </div>
                                    )}

                                    {/* Seniors */}
                                    {lastPrice.senior_amount && (
                                        <div className="flex items-center justify-between text-xs bg-white p-2 rounded">
                                            <span className="font-medium text-gray-600">{service.show_retired ? t('retired_plural') : t('seniors')}</span>
                                            <div className="flex items-center gap-2">
                                                <span className="text-gray-600">{formatPrice(lastPrice.senior_amount)} {currency}</span>
                                                <span className="text-gray-400">→</span>
                                                <span className="font-bold text-green-600">{formatPrice(new_senior_amount)} {currency}</span>
                                            </div>
                                        </div>
                                    )}

                                    {/* Infants */}
                                    {lastPrice.infant_amount && (
                                        <div className="flex items-center justify-between text-xs bg-white p-2 rounded">
                                            <span className="font-medium text-gray-600">{t('infants')}</span>
                                            <div className="flex items-center gap-2">
                                                <span className="text-gray-600">{formatPrice(lastPrice.infant_amount)} {currency}</span>
                                                <span className="text-gray-400">→</span>
                                                <span className="font-bold text-green-600">{formatPrice(new_infant_amount)} {currency}</span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Footer */}
            <div className="mt-8">
                {!hasStarted ? (
                    <div className="flex justify-end">
                        <Button
                            onClick={handleBulkAddPrice}
                            color="black"
                            radius="xl"
                            size="xs"
                        >
                            {t('apply_changes')}
                        </Button>
                    </div>
                ) : allCompleted ? (
                    <div className="flex items-center justify-center gap-2">
                        <div className="text-sm font-medium text-green-600">
                            {t('done')}
                        </div>
                    </div>
                ) : (
                    <div className="flex items-center justify-center gap-2">
                        <div className="text-sm font-medium">
                            {t('updating')} {counter} / {services.length}
                        </div>
                        <Loader size="sm" color="black" />
                    </div>
                )}
            </div>
        </>
    );
}
